import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Input,
  Form,
  Modal,
  Space,
  Popconfirm,
  message,
  Select,
  Switch,
} from "antd";

import {
  GetBoatSets,
  AddNewBoatSet,
  GetBoats,
  AddNewBoat,
  DeleteBoatSet,
  DeleteBoat,
  RenameBoat,
  RenameBoatSet,
  MoveBoat,
  MoveBoatSet,
  SetBoatUnavailable,
  SetBoatAdminBookingOnly,
} from "../../../APIManager";

const { Option } = Select;

const BoatManager = () => {
  const [boatSets, setBoatSets] = useState([]);
  const [loading, setLoading] = useState(false);

  const [boatSetForm] = Form.useForm();
  const [boatForm] = Form.useForm();

  const [addingBoatSetModalVisible, setAddingBoatSetModalVisible] =
    useState(false);
  const [addingBoatModalVisible, setAddingBoatModalVisible] = useState(false);
  const [selectedBoatSet, setSelectedBoatSet] = useState(null); // BoatSet to add boat to

  const [editingBoatSet, setEditingBoatSet] = useState(null);
  const [editingBoat, setEditingBoat] = useState(null);

  const [renameBoatSetForm] = Form.useForm();
  const [renameBoatForm] = Form.useForm();

  const fetchBoatSets = async () => {
    setLoading(true);
    try {
      //Get all boat sets
      const resSets = await GetBoatSets();
      const sets = resSets.data;

      //Get all boats
      const resBoats = await GetBoats();
      const boats = resBoats.data;

      //Map boats into their sets
      const setsWithBoats = sets
        .sort((a, b) => a.SortOrder - b.SortOrder)
        .map((set) => ({
          ...set,
          boats: boats
            .filter((boat) => boat.BoatSet._id === set._id)
            .sort((a, b) => a.SortOrder - b.SortOrder),
        }));

      setBoatSets(setsWithBoats);
      console.log({ setsWithBoats });
    } catch (err) {
      message.error("Failed to fetch boat sets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBoatSets();
  }, []);

  // --- Add BoatSet ---
  const handleAddBoatSet = async (values) => {
    try {
      await AddNewBoatSet({ Name: values.Name });
      message.success("Boat Set added");
      setAddingBoatSetModalVisible(false);
      boatSetForm.resetFields();
      fetchBoatSets();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to add Boat Set");
    }
  };

  // --- Delete BoatSet ---
  const handleDeleteBoatSet = async (setId) => {
    try {
      await DeleteBoatSet({ id: setId });
      message.success("Boat Set deleted");
      fetchBoatSets();
    } catch (err) {
      message.error("Failed to delete Boat Set");
    }
  };

  // --- Add Boat ---
  const handleAddBoat = async (values) => {
    try {
      await AddNewBoat({ ...values, BoatSet: selectedBoatSet._id });
      message.success("Boat added");
      setAddingBoatModalVisible(false);
      boatForm.resetFields();
      fetchBoatSets();
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to add boat");
    }
  };

  // --- Delete Boat ---
  const handleDeleteBoat = async (boatId) => {
    try {
      await DeleteBoat({ id: boatId });
      message.success("Boat deleted");
      fetchBoatSets();
    } catch (err) {
      message.error("Failed to delete boat");
    }
  };

  const handleRenameBoatSet = async (values) => {
    try {
      await RenameBoatSet({
        id: editingBoatSet._id,
        name: values.Name,
      });
      message.success("Boat Set renamed");
      setEditingBoatSet(null);
      fetchBoatSets();
    } catch {
      message.error("Failed to rename Boat Set");
    }
  };

  const handleRenameBoat = async (values) => {
    try {
      await RenameBoat({
        id: editingBoat._id,
        name: values.Name,
      });
      message.success("Boat renamed");
      setEditingBoat(null);
      fetchBoatSets();
    } catch {
      message.error("Failed to rename Boat");
    }
  };

  const handleToggleUnavailable = async (boatId, unavailable) => {
    try {
      await SetBoatUnavailable({ id: boatId, unavailable }); // call your API
      message.success(
        `Boat marked as ${unavailable ? "unavailable" : "available"}`
      );
      fetchBoatSets(); // refresh list
    } catch (err) {
      message.error("Failed to update boat availability");
    }
  };

  const handleToggleAdminBookingOnly = async (boatId, adminOnly) => {
    try {
      await SetBoatAdminBookingOnly({ id: boatId, adminOnly }); // call your API
      message.success(
        `Boat marked as ${
          adminOnly ? "admin booking only" : "available to all"
        }`
      );
      fetchBoatSets(); // refresh list
    } catch (err) {
      message.error("Failed to update boat admin booking status");
    }
  };

  const handleMoveBoatSet = async (id, direction) => {
    try {
      setLoading(true);
      await MoveBoatSet({ id, direction }); // API call
      fetchBoatSets(); // refresh
    } catch (err) {
      setLoading(false);
      message.error(err.response?.data?.message || "Failed to move BoatSet");
    }
  };

  const handleMoveBoat = async (boatId, boatSetId, direction) => {
    try {
      setLoading(true);
      await MoveBoat({ id: boatId, direction }); // API call
      fetchBoatSets(); // refresh
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to move Boat");
    }
  };

  // --- Columns for nested Boats table ---
  const boatColumns = [
    { title: "Boat Name", dataIndex: "Name", key: "Name" },
    { title: "Type", dataIndex: "Type", key: "Type" },

    {
      title: "Admin Only",
      key: "AdminBookingOnly",
      render: (_, record) => (
        <Switch
          checked={record.AdminBookingOnly}
          onChange={(checked) =>
            handleToggleAdminBookingOnly(record._id, checked)
          }
        />
      ),
    },
    {
      title: "Unavailable",
      key: "Unavailable",
      render: (_, record) => (
        <Switch
          checked={record.Unavailable}
          onChange={(checked) => handleToggleUnavailable(record._id, checked)}
        />
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditingBoat(record);
              renameBoatForm.setFieldsValue({ Name: record.Name });
            }}
          >
            Rename
          </Button>
          <Button
            size="small"
            onClick={() => handleMoveBoat(record._id, record.BoatSet._id, "up")}
          >
            ↑
          </Button>
          <Button
            size="small"
            onClick={() =>
              handleMoveBoat(record._id, record.BoatSet._id, "down")
            }
          >
            ↓
          </Button>
          <Popconfirm
            title="Are you sure to delete this boat?"
            onConfirm={() => handleDeleteBoat(record._id)}
          >
            <Button danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // --- Columns for BoatSets table ---
  const boatSetColumns = [
    { title: "Boat Set Name", dataIndex: "Name", key: "Name" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditingBoatSet(record);
              renameBoatSetForm.setFieldsValue({ Name: record.Name });
            }}
          >
            Rename
          </Button>
          <Button
            size="small"
            onClick={() => handleMoveBoatSet(record._id, "up")}
          >
            ↑
          </Button>
          <Button
            size="small"
            onClick={() => handleMoveBoatSet(record._id, "down")}
          >
            ↓
          </Button>

          <Button
            type="primary"
            size="small"
            onClick={() => {
              setSelectedBoatSet(record);
              setAddingBoatModalVisible(true);
            }}
          >
            Add Boat
          </Button>

          <Popconfirm
            title="Are you sure to delete this Boat Set?"
            onConfirm={() => handleDeleteBoatSet(record._id)}
          >
            <Button danger size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Button
        type="primary"
        style={{ marginBottom: 16 }}
        onClick={() => setAddingBoatSetModalVisible(true)}
      >
        Add Boat Set
      </Button>

      <Table
        dataSource={boatSets}
        columns={boatSetColumns}
        rowKey="_id"
        loading={loading}
        expandable={{
          expandedRowRender: (record) => (
            <Table
              dataSource={record.boats}
              columns={boatColumns}
              rowKey="_id"
              pagination={false}
            />
          ),
        }}
      />

      {/* Modal to add BoatSet */}
      <Modal
        title="Add Boat Set"
        visible={addingBoatSetModalVisible}
        onCancel={() => setAddingBoatSetModalVisible(false)}
        onOk={() => boatSetForm.submit()}
      >
        <Form form={boatSetForm} onFinish={handleAddBoatSet} layout="vertical">
          <Form.Item
            label="Boat Set Name"
            name="Name"
            rules={[{ required: true, message: "Please enter boat set name" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal to add Boat */}
      <Modal
        title={`Add Boat to ${selectedBoatSet?.Name}`}
        visible={addingBoatModalVisible}
        onCancel={() => setAddingBoatModalVisible(false)}
        onOk={() => boatForm.submit()}
      >
        <Form form={boatForm} onFinish={handleAddBoat} layout="vertical">
          <Form.Item
            label="Boat Name"
            name="Name"
            rules={[{ required: true, message: "Please enter boat name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Boat Type"
            name="Type"
            rules={[{ required: true, message: "Please select boat type" }]}
          >
            <Select placeholder="Select a boat type">
              <Option value="Single Scull">Single Scull</Option>
              <Option value="Double Scull">Double Scull</Option>
              <Option value="Quad">Quad</Option>
              <Option value="Pair">Pair</Option>
              <Option value="Coxed Four">Coxed Four</Option>
              <Option value="Eight">Eight</Option>
              <Option value="Other">Other</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Rename Boat Set"
        visible={!!editingBoatSet}
        onCancel={() => setEditingBoatSet(null)}
        onOk={() => renameBoatSetForm.submit()}
      >
        <Form
          form={renameBoatSetForm}
          onFinish={handleRenameBoatSet}
          layout="vertical"
        >
          <Form.Item
            label="Boat Set Name"
            name="Name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Rename Boat"
        visible={!!editingBoat}
        onCancel={() => setEditingBoat(null)}
        onOk={() => renameBoatForm.submit()}
      >
        <Form
          form={renameBoatForm}
          onFinish={handleRenameBoat}
          layout="vertical"
        >
          <Form.Item
            label="Boat Name"
            name="Name"
            rules={[{ required: true, message: "Please enter name" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default BoatManager;
